// Connects the clip cache in src/scripts/clipCache.js to the three things it
// deliberately does not import: the phraseReplay flag, the signed-in account,
// and the getAudio request that fetches a clip.
//
// Kept apart from clipCache.js for the same reason playbackRateSync.js is kept
// apart from playbackRate.js: the playback service and the pill import the
// cache, and their unit tests load them without Firebase, the auth store or
// the flag store. Called once from src/main.js, after initFeatureFlags.

import { watch } from "vue";
import { cloudFunctions } from "@/store/cloudFunctions.js";
import { authStore } from "@/store/auth";
import { featureFlags } from "@/store/featureFlags.js";
import {
  clearClipCache,
  clipCacheState,
  registerClipFetcher,
} from "@/scripts/clipCache.js";
import { stopSlicesFor } from "@/scripts/slicePlayback.js";

// The same request AudioPlayer.loadAudio makes for its own copy of a clip:
// the getAudio endpoint with the learner's ID token and the priority hint.
// (PR #615, which moves clips to presigned S3 URLs, is still open; if it
// merges, this request follows AudioPlayer's new path.)
export async function fetchTutorClip(audioId, { priority = "low", signal } = {}) {
  const params = new URLSearchParams({ audioId });
  const token = await authStore.getIdToken();
  const response = await fetch(cloudFunctions.getAudioApi() + "?" + params, {
    method: "GET",
    headers: { Authorization: "Bearer " + token },
    priority,
    signal,
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.blob();
}

let initialized = false;

export function initPhraseReplaySync() {
  if (initialized) return;
  initialized = true;
  registerClipFetcher(fetchTutorClip);
  // A watcher rather than a one-time read: flags start false and flip moments
  // after load (src/store/featureFlags.js), and turning the flag off in the
  // admin panel must stop the pills using the shared fetch at once.
  watch(
    () => featureFlags.phraseReplay === true,
    (enabled) => {
      clipCacheState.enabled = enabled;
      // Turning the flag off also ends a phrase or loop already sounding,
      // rather than leaving it to the next pause or reply.
      if (!enabled) stopSlicesFor();
    },
    { immediate: true }
  );
  // The cache holds the signed-in learner's clips. Signing out, a revoked or
  // expired token, or another account replacing this one all change the uid,
  // and the next learner on this device must not be served the previous one's
  // audio. A sign-in from signed out keeps what is there.
  watch(
    () => authStore.userId,
    (next, previous) => {
      if (!previous || next === previous) return;
      // A phrase slice or loop plays from a cached clip's object URL, which
      // the clear revokes, and it belongs to the account that just left.
      stopSlicesFor();
      clearClipCache();
    }
  );
}
